import * as React from 'react';
import { connect } from 'react-redux';
import isNil from 'lodash/isNil';

import { Table, TableContainer } from 'components/table';
import Pagination from 'components/pagination/Pagination';
import Spinner from 'components/spinner/Spinner';

import CampaignsTableRow from './CampaignsTableRow';
import CampaignsTableEmptyRow from './CampaignsTableEmptyRow';
import CampaignsTableInfluencerHead from './CampaignsTableInfluencerHead';
import CampaignsTableBrandHead from './CampaignsTableBrandHead';
import CampaignsCallToAction from 'modules/campaigns/components/campaigns-empty/call-to-action/CampaignsEmptyCallToAction';

import { ORGANIZATION_PROFILE_TYPE_BRAND, ORGANIZATION_PROFILE_TYPE_INFLUENCER } from 'utils/organization';

import type {
  ApprovalStatus,
  BrandProfile,
  CampaignWithSubcampaignMeta,
  DeliverablesOverdueBySubcampaignIdLocal,
  LocationWindow,
  OrganizationProfileType,
  Pagination as PaginationType
} from 'types';
import type {
  CallToActionType
} from 'modules/campaigns/components/campaigns-empty/call-to-action/types';

type StateProps = {
  location: LocationWindow
};

type OwnProps = {
  influencerId?: number,
  hasProfileBoost?: boolean,
  organizationsListIds?: Array<number>,
  overdueDeliverables?: DeliverablesOverdueBySubcampaignIdLocal,
  campaignsIsLoading: boolean,
  campaignsList: Array<CampaignWithSubcampaignMeta>,
  campaignsPagination?: PaginationType,
  profileType: OrganizationProfileType,
  activeTab: string,
  hasFilters?: boolean,
  handlePageChanged?: (page: number) => void,
  influencerStatus?: ApprovalStatus,
  influencerSubcampaignsCounts?: {
    [x: string]: number
  }
};

type Props = StateProps & OwnProps;

class CampaignsTableByState extends React.Component<Props> {
  isDisabled(brandProfile?: BrandProfile | null): boolean {
    const { organizationsListIds } = this.props;
    if (!organizationsListIds) {
      return false;
    }

    return !!brandProfile
      && (
        isNil(brandProfile.organizationId)
        || organizationsListIds.indexOf(brandProfile.organizationId) === -1
      );
  }

  renderSpinner = (): React.ReactNode => (
    <div className="campaigns-table-by-state__spinner">
      <Spinner
        position="inline"
        size="small"
        color="light"
      />
    </div>
  );

  renderRows = (): React.ReactNode => {
    const {
      campaignsList,
      campaignsIsLoading,
      overdueDeliverables,
      profileType,
      influencerStatus,
      activeTab,
      hasFilters
    } = this.props;

    const campaigns = campaignsList || [];

    if (!campaignsIsLoading && !campaigns.length) {
      return (
        <CampaignsTableEmptyRow
          activeTab={activeTab}
          hasFilters={!!hasFilters}
          profileType={profileType}
        />
      );
    }

    return campaigns.map(campaign => (
      <CampaignsTableRow
        key={`campaign-${String(campaign.id)}-${campaigns.length}`}
        campaign={campaign}
        overdueDeliverables={overdueDeliverables}
        disabled={this.isDisabled(campaign.brandProfile)}
        profileType={profileType}
        influencerStatus={influencerStatus}
      />
    ));
  };

  render() {
    const {
      influencerId,
      campaignsIsLoading,
      campaignsPagination,
      profileType,
      hasProfileBoost,
      activeTab,
      handlePageChanged,
      location,
      influencerStatus,
      influencerSubcampaignsCounts
    } = this.props;

    const shouldShowCTAPending = influencerSubcampaignsCounts?.pending === 0 && activeTab === 'pending';
    const shouldShowCTAAccepted = influencerSubcampaignsCounts?.accepted === 0 && activeTab === 'accepted';

    // call to action graphic / influencer only
    if (!isNil(influencerId) && (shouldShowCTAPending || shouldShowCTAAccepted)) {
      let ctaType = hasProfileBoost ? 'profileApprovedWithBoost' : 'profileApprovedNoBoost';

      if (influencerStatus === 'pending') {
        ctaType = 'profilePending';
      }

      return (
        <div className="campaigns-table-by-state">
          <TableContainer>
            <Table style="none">
              <CampaignsCallToAction
                influencerId={influencerId}
                type={ctaType as CallToActionType}
              />
            </Table>
          </TableContainer>
        </div>
      );
    }

    return (
      <div className="campaigns-table-by-state">
        <TableContainer>
          <Table style="none">
            {
              profileType === ORGANIZATION_PROFILE_TYPE_INFLUENCER && (
                <CampaignsTableInfluencerHead />
              )
            }
            {
              profileType === ORGANIZATION_PROFILE_TYPE_BRAND && (
                <CampaignsTableBrandHead />
              )
            }
            <div className="campaigns-table-by-state__container">
              {
                this.renderRows()
              }
              {
                campaignsIsLoading &&
                  this.renderSpinner()
              }
            </div>
            {
              campaignsPagination && (
                <Pagination
                  className="campaigns-table-by-state__pagination"
                  location={location}
                  meta={campaignsPagination}
                  links={campaignsPagination.links}
                  onClick={handlePageChanged}
                  scrollToTopOnPageChange={false}
                />
              )
            }
          </Table>
        </TableContainer>
      </div>
    );
  }
}

const mapStateToProps = ({ router }): StateProps => ({
  location: router.location
});

export default connect(mapStateToProps)(CampaignsTableByState);
